import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plane, Compass, Calendar, ConciergeBell } from 'lucide-react';
import { isPhase2Released } from '../utils/featureGates';

const BLUE_ENVELOPE_URL = 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/dkrpi9j5_blue%20envelope%20no%20BG.png';

const mainLinks = [
  { label: 'Home',  id: 'hero' },
  { label: 'Video', id: 'video' },
];

// Discover section — order matches carousel tile order top-to-bottom
const discoverLinks = [
  { label: 'Calendar', route: '/calendar',    icon: Calendar,       scrollId: null },
  { label: 'Oscar',    route: null,            icon: ConciergeBell,  scrollId: 'explore' },
  { label: 'NJ Guide', route: '/things-to-do', icon: Compass,        scrollId: null },
  { label: 'Travel',   route: '/travel',        icon: Plane,          scrollId: null },
];

const SHOW_EXPLORE = isPhase2Released();

const GOLD      = 'rgba(212,168,83,0.85)';
const GOLD_DIM  = 'rgba(212,168,83,0.55)';
const BEIGE     = 'rgba(245,232,195,0.90)';
const BEIGE_DIM = 'rgba(245,232,195,0.68)';

// Tapered gold separator — fades to transparent at both ends
const TaperedSeparator = ({ margin = '0' }) => (
  <div style={{
    height: 1,
    margin,
    background: 'linear-gradient(90deg, transparent 0%, rgba(212,168,83,0.55) 22%, rgba(212,168,83,0.55) 78%, transparent 100%)',
  }} />
);

const SlideMenu = ({ open, onClose, scrollTo }) => {
  const navigate = useNavigate();

  const handleDiscoverClick = (link) => {
    onClose();
    if (link.scrollId) {
      scrollTo(link.scrollId);
    } else if (link.route) {
      navigate(link.route);
    }
  };

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200 }}
        />
      )}

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: open ? 0 : -300, width: 280, height: '100%', zIndex: 201,
        background: '#000000',
        boxShadow: open ? '-4px 0 32px rgba(0,0,0,0.50)' : 'none',
        transition: 'right 0.3s ease', display: 'flex', flexDirection: 'column',
      }}>

        {/* Header — dark blue envelope logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' }}>
          <div style={{ width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img
              src={BLUE_ENVELOPE_URL}
              alt="M-Vitations"
              style={{ width: 52, height: 52, objectFit: 'contain' }}
            />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={22} color={BEIGE} />
          </button>
        </div>

        <TaperedSeparator />

        {/* Navigation */}
        <div style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>

          {/* Main links */}
          {mainLinks.map(l => (
            <div
              key={l.id}
              onClick={() => { onClose(); scrollTo(l.id); }}
              style={{
                padding: '18px 24px',
                fontFamily: "'Cinzel', serif",
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: '1px',
                color: BEIGE,
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                borderLeft: '4px solid transparent',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(212,168,83,0.10)';
                e.currentTarget.style.borderLeftColor = GOLD;
                e.currentTarget.style.color = GOLD;
                e.currentTarget.style.fontWeight = '600';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderLeftColor = 'transparent';
                e.currentTarget.style.color = BEIGE;
                e.currentTarget.style.fontWeight = '500';
              }}
            >
              {l.label}
            </div>
          ))}

          {/* Discover section */}
          {SHOW_EXPLORE && (
            <>
              <div style={{ paddingTop: 8, marginTop: 4 }}>
                <TaperedSeparator margin="0 20px 10px" />
                <div style={{
                  padding: '6px 24px 10px',
                  fontSize: 10,
                  fontFamily: "'Cinzel', serif",
                  fontWeight: 700,
                  color: GOLD_DIM,
                  letterSpacing: '2.5px',
                  textTransform: 'uppercase',
                }}>
                  Discover
                </div>
              </div>

              {discoverLinks.map(l => {
                const Icon = l.icon;
                return (
                  <div
                    key={l.label}
                    onClick={() => handleDiscoverClick(l)}
                    style={{
                      padding: '16px 24px',
                      fontFamily: "'Cinzel', serif",
                      fontSize: 18,
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      color: BEIGE_DIM,
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      borderLeft: '4px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(212,168,83,0.10)';
                      e.currentTarget.style.borderLeftColor = GOLD;
                      e.currentTarget.style.color = GOLD;
                      e.currentTarget.style.fontWeight = '600';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.color = BEIGE_DIM;
                      e.currentTarget.style.fontWeight = '500';
                    }}
                  >
                    <Icon size={18} />
                    <span>{l.label}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <TaperedSeparator />
        <div style={{ padding: '16px 20px', textAlign: 'center' }}>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 12,
            color: 'rgba(245,232,195,0.48)',
            lineHeight: 1.75,
            letterSpacing: '0.5px',
          }}>
            A Birthday Celebrating<br />
            80 Years of Connection<br />
            Saturday, July 25th, 2026
          </div>
        </div>

      </div>
    </>
  );
};

export default SlideMenu;
